import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { relativeTime } from '@/lib/format'
import { useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { ConnRow } from './ConnRow'
import { ExtensionPanel } from './ExtensionPanel'
import { GOOGLE_DOCS } from '@/lib/docs'
import { Ga4Card } from './Ga4Card'
import { ga4State } from './ga4-state'
import { SearchConsoleCard } from './SearchConsoleCard'
import { SenderCard } from './SenderCard'

/**
 * Connections — S-D3's panel, and the screen where "connect LinkedIn" becomes
 * one thing again.
 *
 * **The whole reason this panel exists in this shape.** A tenant thinks they
 * have *a LinkedIn connection*. AugRank has two mechanisms with nothing in
 * common: an OAuth grant that lets it post (S-B17), and a browser extension that
 * lets it search from the tenant's own session (S-B14). They are granted
 * differently, they fail differently, and either can be missing while the other
 * works. Putting them on separate screens would mean a tenant whose posts go out
 * and whose discovery does not — or the reverse — having no single place that
 * says so. So the LinkedIn card carries both halves, and each one says plainly
 * what it does and does not cover.
 *
 * **The extension panel is rendered as a card inside this one**, which is what
 * `panels.ts` said would happen when Connections arrived. `/settings/extension`
 * still resolves — it redirects here — because notification emails sent before
 * this release carry that path.
 *
 * **Reconnect is the ordinary case, not an error path.** A LinkedIn access token
 * lasts sixty days and a Share on LinkedIn app is issued no refresh token, so
 * every tenant who keeps using the product comes back through the connect
 * button. The card says *when* it expires rather than waiting for a post to
 * fail, and `expiring` is true ten minutes early so the prompt is never late.
 *
 * **The risk acknowledgement is a checkbox that gates the button, and the server
 * does not rely on it.** Pressing Connect *is* the acknowledgement — the route
 * records it before the browser leaves — so the checkbox is here to make sure
 * nobody presses it without reading, not to carry a flag.
 *
 * The three states every component ships (CLAUDE.md, Frontend): loading while
 * the connection is read, an error state with a retry, and — the one that
 * matters most here — the empty state, which is what a freshly provisioned
 * tenant actually sees and is written as an invitation rather than as a warning.
 */

/** Mirrors `_ERRORS` in `api/app/v1/connections.py`: what the outcome query means. */
const OUTCOMES: Record<string, { tone: 'success' | 'danger'; title: string; body: string }> = {
  connected: {
    tone: 'success',
    title: 'LinkedIn connected',
    body: 'Posts can now be published to the account you authorised.',
  },
  failed: {
    tone: 'danger',
    title: 'LinkedIn did not finish connecting',
    body:
      'Nothing was changed. This usually means the consent screen was cancelled, or it was ' +
      'left open too long. Try again — it takes a few seconds.',
  },
}

/** How the five publish targets are named and lettered in the row list.
 *
 * `download` was missing from both maps and its row read *"download —
 * augmentrait.com · Action needed · no credential stored"* (walkthrough
 * 2026-09-12): the raw enum as a title, and a warning about a credential the
 * target does not have. `app/v1/seo_publish.py` says it plainly — for
 * `download`, `credential_set: false` **is** the finished state. */
const TARGET_LABELS: Record<string, string> = {
  git: 'Git repository',
  wordpress: 'WordPress',
  webflow: 'Webflow',
  shopify: 'Shopify',
  download: 'Files to download',
}

const TARGET_LOGOS: Record<string, string> = {
  git: 'G',
  wordpress: 'W',
  webflow: 'Wf',
  shopify: 'S',
  download: '↓',
}

/** Targets that authenticate to nothing, so "no credential" is not a state to fix. */
const CREDENTIAL_FREE_TARGETS = new Set(['download'])

/**
 * SHELL-46. The sender's DNS state as S-D3's one line — *"SPF verified · DKIM
 * pending"*.
 *
 * Each mechanism is named with its own verdict rather than the domain being
 * given a single word, because the two fail for different reasons and are fixed
 * by publishing different records. A sender whose SPF passes and whose DKIM does
 * not is a *specific* problem with a *specific* fix, and "Action needed" alone
 * sends somebody to a screen with no idea what they are looking for.
 */
function dnsLine(sender: Schema<'SenderOut'>): string {
  const words: Record<string, string> = {
    pass: 'verified',
    warn: 'pending',
    fail: 'failing',
    unknown: 'not checked',
  }
  const parts = sender.checks.map(
    (check) => `${check.mechanism.toUpperCase()} ${words[check.status] ?? check.status}`,
  )
  return parts.length > 0 ? parts.join(' · ') : 'No DNS records checked yet'
}

export function ConnectionsPanel(): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  const { tenant } = useActiveTenant()
  const [params, setParams] = useSearchParams()
  const [acknowledged, setAcknowledged] = useState(false)
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)
  // SHELL-55. The §8 risk acknowledgement, now a dialog rather than a checkbox
  // in the card body — the row has one small action and the acknowledgement has
  // to be read before it is pressed. What it gates is unchanged: *pressing
  // Connect is the acknowledgement*, the route records it before the browser
  // leaves, and this exists so nobody presses without reading.
  const [acknowledging, setAcknowledging] = useState(false)

  // The Google grant, read once for the two cards built on it. It was the
  // Search Console card's own query until S-G13 added the GA4 card over the
  // same grant — two copies of one connection let a Disconnect on one card
  // leave the other still showing a credential that had just been deleted.
  const googleQuery = useQuery(
    (signal) => call(() => api.GET('/api/v1/connections/google', { signal })),
    [],
  )
  const now = new Date()

  // The Google grant's own controls, which used to live in `SearchConsoleCard`.
  // They are here because the row that offers them is here — see SHELL-55.
  const googleStart = useMutation(() =>
    call(() => api.POST('/api/v1/connections/google/start', {})),
  )
  const googleDisconnect = useMutation(() =>
    call(() => api.DELETE('/api/v1/connections/google', {})),
  )

  const linkedin = useQuery(
    (signal) => call(() => api.GET('/api/v1/connections/linkedin', { signal })),
    [],
  )

  // SHELL-46. The sending identity's SPF/DKIM state, which lived only on the
  // Outreach dashboard — so the panel a tenant opens to answer *"what is
  // connected"* was the one screen in the product that did not mention the
  // connection their outreach actually goes out through.
  const deliverability = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/deliverability', { signal })),
    [],
  )

  // The site list, read once for the GA4 row and the GA4 card: whether a
  // property is chosen is a fact about a site, not about the grant, and the row
  // used to say *Connected* off the grant alone while the card below it said
  // *No property chosen* off this.
  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])

  // The paired browsers, for the extension row. The row said *Pair* under a
  // card that listed a paired browser (walkthrough 2026-09-12): it was the one
  // row in the list whose state was not read from anywhere. The card below
  // reads the same route; a second request here is cheaper than lifting the
  // card's state up through a panel that otherwise knows nothing about it.
  const devices = useQuery(
    (signal) => call(() => api.GET('/api/v1/extension/devices', { signal })),
    [],
  )
  const paired = (devices.state.data?.devices ?? []).filter((row) => row.revoked_at === null)
  const lastSeen = paired
    .map((row) => row.last_seen_at)
    .filter((seen): seen is string => seen !== null)
    .sort()
    .at(-1)

  // SHELL-47. One row per site's publish target. One request, not one per site
  // — see `list_targets` on why the loop is the server's.
  const targets = useQuery(
    (signal) => call(() => api.GET('/api/v1/seo/publish-targets', { signal })),
    [],
  )

  const start = useMutation(() => call(() => api.POST('/api/v1/connections/linkedin/start', {})))

  const disconnect = useMutation(() => call(() => api.DELETE('/api/v1/connections/linkedin', {})))

  // The callback redirects back here with `?linkedin=connected|failed`, because
  // a redirect from LinkedIn carries no body of ours. Read once, shown as a
  // toast, and then stripped from the URL — a reload should not re-announce a
  // connection that happened ten minutes ago.
  const outcome = params.get('linkedin')
  useEffect(() => {
    if (outcome === null) return
    const message = OUTCOMES[outcome]
    if (message !== undefined) {
      toast.show({ kind: message.tone, title: message.title, body: message.body })
    }
    params.delete('linkedin')
    setParams(params, { replace: true })
    linkedin.refetch()
    // `outcome` is the only input that should re-run this; re-running it on a
    // refetched connection would show the toast again on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome])

  const connection = linkedin.state.data
  // Pulled out of the JSX because the generated type is `string | null |
  // undefined` — the field is optional on the schema — and narrowing it inline
  // would leave `relativeTime` taking a possibly-undefined argument.
  const expiresAt = connection?.expires_at ?? null
  const pages = connection?.pages ?? []

  const google = googleQuery.state.data
  // The GA4 row's one answer, shared with the card — see `ga4State`. The badge
  // waits for both reads for the reason the card gives: briefly wrong is worse
  // than briefly absent.
  const ga4 = ga4State(google, sites.state.data?.sites ?? [])
  // The domain the Google help line names. One site is the common case; with
  // several, "your site" is truer than naming the first.
  const listedSites = sites.state.data?.sites ?? []
  const tenantSite = listedSites.length === 1 ? listedSites[0]?.domain : 'your site'
  const ga4Settled = googleQuery.state.status === 'ready' && sites.state.status === 'ready'
  // Every sending identity, not the first. The mockup draws one because its
  // example tenant has one; showing `senders[0]` would leave a tenant with two
  // domains looking at a green row while the other one bounced — a silent cap
  // on exactly the panel whose job is to say what is and is not working.
  const senders = deliverability.state.data?.senders ?? []
  // Narrowed once rather than at each branch: `detail` is optional on the
  // generated schema, so it is `string | null | undefined` and a `=== null`
  // check inside a template literal does not narrow away the third one.
  const refusal =
    connection?.detail === null || connection?.detail === undefined
      ? ''
      : ` · LinkedIn said: ${connection.detail}`
  const publishTargets = targets.state.data?.targets ?? []

  return (
    <div className="ar-stack">
      {/*
        SHELL-55. **One card of rows, which is what S-D3 draws.** The panel was
        five separate `ar-card`s with headers and multi-paragraph prose, so the
        question this screen exists to answer — *what is connected* — took a page
        of scrolling and comparing badges in five different places.

        The row is `.app-conn`, already the port of the mockup's `.sc-conn` and
        already in use on the SEO and Outreach screens. Six rows rather than
        five: SHELL-46 adds the sending identity and SHELL-47 the publish
        targets, which the design draws and the panel did not have.

        **Each connection has exactly one place to press.** The cards below are
        configuration — which property a site reads, how the extension is paired
        — and their connect and disconnect controls are gone, because two
        buttons doing one thing is how a tenant ends up pressing the one that
        does not carry the risk acknowledgement.
      */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Connections</h2>
        </div>

        <div className="ar-card__body ar-stack ar-stack--tight">
          <InlineError
            failure={start.state.failure ?? disconnect.state.failure ?? googleStart.state.failure}
          />

          {/* --- Google Search Console ------------------------------------- */}
          {googleQuery.state.status === 'loading' ? (
            <Loading label="Loading your Google connection" />
          ) : googleQuery.state.status === 'error' ? (
            <ErrorState failure={googleQuery.state.failure} onRetry={googleQuery.refetch} />
          ) : (
            <ConnRow
              logo="G"
              title="Google Search Console"
              /*
                SHELL-58. S-D3's metadata line is *"account · read-only ·
                connected 5 days ago"*, and the card carried only "Reading as
                <account>." — so the two facts a tenant checks when search data
                looks stale, that it is read-only and how long it has been
                connected, were on neither screen. `connected_at` was already on
                the wire and rendered nowhere.
              */
              status={
                google?.connected === true
                  ? `${google.account_label ?? 'Connected account'} · read-only${
                      google.connected_at === null || google.connected_at === undefined
                        ? ''
                        : ` · connected ${relativeTime(google.connected_at, now)}`
                    }`
                  : google?.status === 'no_access'
                    ? 'Access withdrawn · your SEO dashboard has no ranking data'
                    : 'Not connected · your SEO dashboard has no ranking or click data'
              }
              /*
                The one prerequisite Connect cannot check for the tenant: the
                Google account has to already have the site in Search Console.
                Said under the row, with the page that explains it — the row
                had a Connect button and no word on which account to press it
                with.
              */
              help={
                google?.connected === true ? null : (
                  <>
                    Sign in with the Google account that already has {tenantSite} in Search Console.{' '}
                    <a
                      className="ar-link"
                      href={GOOGLE_DOCS.beforeYouStart}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Which account, and how to verify a site ↗
                    </a>
                  </>
                )
              }
              badge={
                google?.connected === true ? (
                  <span className="ar-badge ar-badge--success">✓ Connected</span>
                ) : google?.status === 'no_access' ? (
                  <span className="ar-badge ar-badge--danger">Access withdrawn</span>
                ) : null
              }
              action={
                google?.connected === true ? (
                  /* SHELL-56. `quiet`, not `ghost` — the mockup's Disconnect is
                     borderless, and a bordered button beside a green badge reads
                     as the row's primary action. */
                  <Button
                    tone="quiet"
                    size="sm"
                    busy={googleDisconnect.pending}
                    busyLabel="Disconnecting"
                    onClick={() => {
                      void (async () => {
                        const result = await googleDisconnect.run(undefined)
                        if (!result.ok) return
                        googleQuery.refetch()
                        toast.show({
                          kind: 'success',
                          title: 'Search Console disconnected',
                          body: 'The stored Google credential is deleted, and so is the link between each of your sites and its property.',
                        })
                      })()
                    }}
                  >
                    Disconnect
                  </Button>
                ) : (
                  /* SHELL-57. `ghost sm`. It was `primary` at the default
                     control height, which made one row of a six-row list twice
                     the visual weight of the others for no reason but that it
                     happened to be disconnected. */
                  <Button
                    tone="ghost"
                    size="sm"
                    busy={googleStart.pending}
                    busyLabel="Opening Google"
                    onClick={() => {
                      void (async () => {
                        const result = await googleStart.run(undefined)
                        if (!result.ok) return
                        window.location.assign(result.data.url)
                      })()
                    }}
                  >
                    Connect
                  </Button>
                )
              }
            />
          )}

          {/* --- LinkedIn --------------------------------------------------- */}
          {linkedin.state.status === 'loading' ? (
            <Loading label="Loading your LinkedIn connection" />
          ) : linkedin.state.status === 'error' ? (
            <ErrorState failure={linkedin.state.failure} onRetry={linkedin.refetch} />
          ) : (
            <ConnRow
              logo="in"
              title="LinkedIn"
              /*
                The row carries LinkedIn's own last refusal when there is one.
                The configuration card below renders only for a live grant, so
                without this a paused or rejected account would lose the one
                sentence that says *what LinkedIn actually objected to* —
                which is the only thing that tells a tenant whether to wait,
                clear a challenge, or reconnect.
              */
              status={
                connection?.connected === true
                  ? `Publishing as ${connection.account_label ?? 'your LinkedIn account'}${
                      expiresAt === null
                        ? ''
                        : ` · authorisation ends ${relativeTime(expiresAt, now)}`
                    }`
                  : connection?.paused === true
                    ? `LinkedIn interrupted this account · clear it in your own browser${refusal}`
                    : `Not connected · outreach lead discovery is off${refusal}`
              }
              badge={
                connection?.connected === true && !connection.expiring ? (
                  <span className="ar-badge ar-badge--success">✓ Connected</span>
                ) : connection?.paused === true ? (
                  <span className="ar-badge ar-badge--danger">Paused</span>
                ) : connection?.expiring === true ? (
                  <span className="ar-badge ar-badge--warning">⚠ Reconnect needed</span>
                ) : null
              }
              action={
                connection === null ? null : connection.connected && !connection.expiring ? (
                  <Button
                    tone="quiet"
                    size="sm"
                    onClick={() => {
                      setConfirmingDisconnect(true)
                    }}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    tone="ghost"
                    size="sm"
                    onClick={() => {
                      setAcknowledging(true)
                    }}
                  >
                    {connection.connected ? 'Reconnect' : 'Connect'}
                  </Button>
                )
              }
            />
          )}

          {/* --- the sending identity (SHELL-46) ---------------------------- */}
          {deliverability.state.status === 'loading' ? (
            <Loading label="Loading your sending identity" />
          ) : deliverability.state.status === 'error' ? (
            <ErrorState failure={deliverability.state.failure} onRetry={deliverability.refetch} />
          ) : senders.length === 0 ? (
            <ConnRow
              logo="✉"
              title="Sender"
              status="Not set up · outreach email cannot go out"
              /* The card that adds one is on this page, under the extension.
                 This went to `/outreach/health`, a route that has not existed
                 since the health panel moved onto the overview — every *Set up*
                 press landed on the not-found screen (walkthrough 2026-09-12). */
              action={
                <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="#sender">
                  Set up
                </Link>
              }
            />
          ) : (
            senders.map((sender) => (
              <ConnRow
                key={sender.address}
                logo="✉"
                title={`Sender — ${sender.address}`}
                /*
                  A connection that never completed is not a deliverability
                  verdict. The wizard records the address on a `failed` row
                  when the provider refused it — on a deployment with no
                  sending provider at all, every attempt does — and reading
                  that row's domain for SPF and DKIM produced "Not delivering ·
                  SPF failing" for an address nothing ever tried to send from.
                  The DNS is real, but it is not the reason, and "Fix" led to a
                  screen that could not. See `SenderOut.connection_status`.
                */
                status={
                  sender.connection_status === 'failed'
                    ? 'Setting up did not finish · outreach email cannot go out from this address yet'
                    : dnsLine(sender)
                }
                badge={
                  sender.connection_status === 'failed' ? (
                    <span className="ar-badge ar-badge--warning">⚠ Action needed</span>
                  ) : sender.status === 'pass' ? (
                    <span className="ar-badge ar-badge--success">✓ Connected</span>
                  ) : sender.status === 'fail' ? (
                    <span className="ar-badge ar-badge--danger">✕ Not delivering</span>
                  ) : (
                    <span className="ar-badge ar-badge--warning">⚠ Action needed</span>
                  )
                }
                /*
                  A link, not a button, and it goes to the screen that can
                  actually fix it. *Fix* opens the deliverability panel on the
                  overview, where the DNS records are shown with the exact
                  values to publish — reproducing them here would be a second
                  copy of the one thing that must not drift. *Set up* is the
                  wizard's abandoned address, and the thing to do with it is add
                  it as a sending address in the card below on this page.
                */
                action={
                  <Link
                    className="ar-btn ar-btn--ghost ar-btn--sm"
                    to={
                      sender.connection_status === 'failed' ? '#sender' : '/outreach#deliverability'
                    }
                  >
                    {sender.connection_status === 'failed' ? 'Set up' : 'Fix'}
                  </Link>
                }
              />
            ))
          )}

          {/* The bound the API applied, reported rather than swallowed: a panel
              showing three of a tenant's five senders with no note is a panel
              saying the other two are fine. */}
          {deliverability.state.data?.truncated_to ? (
            <p className="ar-field__help" style={{ margin: 0 }}>
              Showing the first {deliverability.state.data.truncated_to} sending addresses. The rest
              are on <Link to="/outreach#deliverability">the outreach overview</Link>.
            </p>
          ) : null}

          {/* --- publish targets (SHELL-47) --------------------------------- */}
          {targets.state.status === 'loading' ? (
            // Its own skeleton rather than nothing: the route costs one
            // Parameter Store call per configured site, so these rows land
            // later than the ones above them and a silent gap reads as
            // *"nothing is configured"* — which is the one thing this row must
            // not say while it is still finding out.
            <Loading label="Loading where you publish" />
          ) : targets.state.status === 'error' ? (
            <ErrorState failure={targets.state.failure} onRetry={targets.refetch} />
          ) : (
            publishTargets.map((entry) => (
              <ConnRow
                key={entry.site_id}
                logo={TARGET_LOGOS[entry.target ?? ''] ?? '↗'}
                title={
                  entry.target === null
                    ? entry.domain
                    : `${TARGET_LABELS[entry.target] ?? entry.target} — ${entry.domain}`
                }
                status={
                  entry.target === null
                    ? 'No publish target · approved articles have nowhere to go'
                    : CREDENTIAL_FREE_TARGETS.has(entry.target)
                      ? 'Publish target · approved articles are downloaded from the queue'
                      : `Publish target · ${entry.credential_set ? 'credential stored' : 'no credential stored'}`
                }
                badge={
                  entry.target === null ? null : entry.credential_set ||
                    CREDENTIAL_FREE_TARGETS.has(entry.target) ? (
                    <span className="ar-badge ar-badge--success">✓ Connected</span>
                  ) : (
                    <span className="ar-badge ar-badge--warning">⚠ Action needed</span>
                  )
                }
                /*
                  *Configure*, where S-D3 draws *Test*. There is no
                  target-connectivity check anywhere in the product — each of the
                  four targets authenticates differently and none of them has a
                  probe — and a Test button that reported nothing would be the
                  interface asserting a control it does not have. The press that
                  exists is the one that fixes what the badge is complaining
                  about.
                */
                action={
                  <Link className="ar-btn ar-btn--quiet ar-btn--sm" to="/settings/publish-target">
                    Configure
                  </Link>
                }
              />
            ))
          )}

          {/* --- Google Analytics 4 ----------------------------------------- */}
          <ConnRow
            logo="GA"
            title="Google Analytics 4"
            /*
              *Connect* when there is no Google grant (parity P-CONN-04,
              2026-09-13 — the mockup's button). Analytics rides on the single
              Google grant, so the button starts the same Google sign-in the row
              above does; NC-7 had left it off for that reason, and the owner
              chose the drawing.
            */
            status={
              ga4 === 'reading'
                ? 'Rides on the Google connection above · reading traffic'
                : ga4 === 'no_property'
                  ? 'Rides on the Google connection above · no Analytics property chosen yet'
                  : ga4 === 'not_granted'
                    ? 'Rides on the Google connection above · Analytics permission not granted'
                    : 'Not connected · revenue attribution unavailable'
            }
            badge={
              // Parity P-CONN-05: the mockup's two badges, *✓ Connected* and
              // *⚠ Action needed*; the status line beside says which action.
              !ga4Settled ? null : ga4 === 'reading' ? (
                <span className="ar-badge ar-badge--success">✓ Connected</span>
              ) : ga4 === 'no_property' || ga4 === 'not_granted' ? (
                <span className="ar-badge ar-badge--warning">⚠ Action needed</span>
              ) : null
            }
            action={
              ga4 === 'no_property' ? (
                // The picker is on the Analytics card further down this panel
                // (settings consolidation, batch 2).
                <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="#analytics-properties">
                  Choose
                </Link>
              ) : ga4Settled && google?.connected !== true ? (
                <Button
                  tone="ghost"
                  size="sm"
                  busy={googleStart.pending}
                  busyLabel="Opening Google"
                  onClick={() => {
                    void (async () => {
                      const result = await googleStart.run(undefined)
                      if (!result.ok) return
                      window.location.assign(result.data.url)
                    })()
                  }}
                >
                  Connect
                </Button>
              ) : undefined
            }
          />

          {/* --- the browser extension --------------------------------------- */}
          <ConnRow
            logo="⌘"
            title="Browser extension"
            status={
              paired.length === 0
                ? 'Finds leads from your own LinkedIn session. Paired in the card below.'
                : `${String(paired.length)} ${paired.length === 1 ? 'browser' : 'browsers'} paired${
                    lastSeen ? ` · last seen ${relativeTime(lastSeen, now)}` : ''
                  }${
                    // 2026-09-12. A paired browser on a trial is refused every
                    // search it asks for (`app/billing/trial.py`), and until
                    // this row said so the only place that showed was the
                    // extension's own popup.
                    tenant.automation_available ? '' : ' · searches wait for a subscription'
                  }`
            }
            badge={
              paired.length > 0 && !tenant.automation_available ? (
                <span className="ar-badge ar-badge--warning">⚠ Action needed</span>
              ) : paired.length > 0 ? (
                <span className="ar-badge ar-badge--success">✓ Connected</span>
              ) : undefined
            }
            /*
              The card is below, but below a LinkedIn card and a GA4 card, so on
              a laptop it is off the bottom of the screen and "below" points at
              nothing the tenant can see. A jump link, not a route: it is the
              same page.
            */
            action={
              <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="#extension">
                {paired.length === 0 ? 'Pair' : 'Manage'}
              </Link>
            }
          />
        </div>
      </section>

      {/*
        SHELL-55 moved LinkedIn's *connection* into the row above and left this,
        which is not connection state: what the grant lets AugRank publish to,
        how much of today's allowance is spent, and the one link in the product
        that reaches the composer. None of it fits on a one-line row, and all of
        it would have been lost by collapsing the card into one.

        Rendered only while there is a grant. There is nothing to say about the
        posting budget of an account that is not connected, and the row above
        already says it is not.
      */}
      {connection?.connected === true ? (
        <section className="ar-card">
          <div className="ar-card__header">
            <h2>Publishing to LinkedIn</h2>
          </div>
          <div className="ar-card__body">
            {connection.expiring ? (
              <p className="ar-callout ar-text-body-sm" data-tone="warning">
                This authorisation has run out or is about to. Reconnect in the list above and
                nothing you have queued will be missed — LinkedIn asks you to renew it every sixty
                days.
              </p>
            ) : null}
            {pages.length > 0 ? (
              <p className="ar-text-body-sm ar-muted">
                Company Pages you can publish to: {pages.map((page) => page.name).join(', ')}.
              </p>
            ) : null}
            <p className="ar-text-body-sm ar-muted">
              {brand.productName} publishes at most {String(connection.max_posts_per_day)} posts a
              day to this account, spaced out through the day. {String(connection.posts_today)} used
              today, {String(connection.posts_remaining_today)} left.
            </p>
            {connection.detail !== null ? (
              <p className="ar-text-body-sm ar-muted">LinkedIn said: {connection.detail}</p>
            ) : null}
            {/*
              S-G9. The compose screen's entry point, and the only one:
              `outreach.html#post` is not one of S-D3's seven Outreach sections,
              so it has no sidebar row and this card is how it is found.
            */}
            <div>
              <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/outreach/post">
                Write a post
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      {/*
        S-B14's panel. It carries the pause banner and the resume button, which
        is why the pause notification points at this route: a challenge stops
        posting and searching together.
      */}
      <ExtensionPanel />

      {/*
        S-B22a, now configuration only: which property each site resolved to.
        Connecting and disconnecting moved to the row above — see SHELL-55.
      */}
      <SearchConsoleCard connection={googleQuery} />

      {/*
        S-G13, audit row §G28. The *same* Google grant seen from the other side.
      */}
      <Ga4Card connection={googleQuery} sites={sites} />

      {/*
        The sending identity, which this panel's API docstring has anticipated
        since S-B18. It takes no `connection` prop because it is not an OAuth
        grant: there is no consent screen and no callback, only an address and a
        confirmation link somebody has to click in their own inbox.
      */}
      <SenderCard />

      {acknowledging ? (
        <Modal
          title="Connect LinkedIn"
          onClose={() => {
            setAcknowledging(false)
            setAcknowledged(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setAcknowledging(false)
                  setAcknowledged(false)
                }}
              >
                Cancel
              </Button>
              <Button
                writes
                busy={start.pending}
                busyLabel="Opening LinkedIn"
                disabled={!acknowledged}
                onClick={() => {
                  void (async () => {
                    const result = await start.run(undefined)
                    if (!result.ok) return
                    // A full navigation, not a fetch: the consent screen is
                    // LinkedIn's page and has to be rendered by the browser.
                    // The `state` cookie the server just set travels with it.
                    window.location.assign(result.data.url)
                  })()
                }}
              >
                Continue to LinkedIn
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            Two separate permissions, and you can have one without the other.{' '}
            <strong>Posting</strong> uses LinkedIn&rsquo;s own authorisation, which is what this
            grants. <strong>Finding leads</strong> runs in your own browser through the browser
            extension. Neither one reads your LinkedIn password or session.
          </p>
          <label className="ar-choice ar-choice--check">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => {
                setAcknowledged(event.target.checked)
              }}
            />
            {/*
              `.ar-choice` hides the native input and draws this box in its
              place, so the span is the control as far as the eye is concerned
              and the input is still the control as far as the keyboard and the
              accessibility tree are concerned. It has to be the input's
              immediate next sibling — that adjacency is what carries `:checked`
              and `:focus-visible` across.
            */}
            <span className="ar-choice__box" aria-hidden="true" />
            <span>
              I understand that automating LinkedIn is against its User Agreement, and that LinkedIn
              can restrict or permanently ban the account {brand.productName} acts as.
            </span>
          </label>
          {connection?.acknowledged_version !== null &&
          connection?.acknowledged_version !== undefined ? (
            <p className="ar-text-body-sm ar-muted">
              Previously acknowledged · version {connection.acknowledged_version}
            </p>
          ) : null}
        </Modal>
      ) : null}

      {confirmingDisconnect ? (
        <Modal
          title="Disconnect LinkedIn?"
          onClose={() => {
            setConfirmingDisconnect(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setConfirmingDisconnect(false)
                }}
              >
                Keep it connected
              </Button>
              <Button
                tone="danger"
                writes
                busy={disconnect.pending}
                busyLabel="Disconnecting"
                onClick={() => {
                  void (async () => {
                    const result = await disconnect.run(undefined)
                    setConfirmingDisconnect(false)
                    if (!result.ok) return
                    setAcknowledged(false)
                    linkedin.refetch()
                    toast.show({ kind: 'success', title: 'LinkedIn disconnected' })
                  })()
                }}
              >
                Disconnect
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {brand.productName} will delete the authorisation LinkedIn gave it and will stop
            publishing to this account. Anything already queued will not go out.
          </p>
          <p>
            Finding leads through the browser extension is <strong>not</strong> affected — that is a
            separate permission, in the card below.
          </p>
        </Modal>
      ) : null}
    </div>
  )
}
